import './index.less';
import { cloneDeep, debounce, isNumber, max, min } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import CurPenSvg from '@/assets/cur-pen.svg';
import {
    BaseStagePlugin,
    getFramework,
    MoveListenerLevel,
    trackConfig,
    useActiveFrameIndex,
    useIdentity,
    useMoveEventListener,
} from '@/framework';
import { useLocaleStore } from '@/i18n/index';
import type { Displacement } from '@/plugins/displacement';
import {
    applyDrawFrameTarget,
    cancelCurrentDraw,
    resolveDrawFrameTarget,
    resolveDrawingFrameId,
} from '@/utils/draw-frame-kit';
import { isHotKeyPress, type ITouchKitEvent } from '@/utils/event-kit';
import { getNewCenterPoint, setLayerRuntimeValue } from '@/utils/layer-kit';
import { getActualAngle, getPointsBetweenAngle, subPoint, translatePointAngle } from '@/utils/math-kit';
import {
    BaseLayerViewType,
    DirectionType,
    LayerType,
    MarkupType,
    MaterialIdsType,
    PluginType,
    ResizeOperateType,
    type IExportPlugin,
    type ILayerProps,
    type IMarkupAnchor,
    type IMarkupDrawConfig,
    type IMarkupLayerProps,
    type IMarkupPoint,
    type IPoint,
} from '@/version-types';
import { prepareMarkupLayerDocumentFonts } from '../../font-measurement';
import type { MarkupLayer } from '../../index';
import {
    getDefaultMarkupPoint,
    getMarkupDefaultLayer,
    LabelKit,
    MARKUP_POINT_SIZE,
    updateScreenshotUrl,
} from '../../utils';
import { adaptDrawMarkupConfig, applyDrawLabelLayout, getDrawArrowLabelSize } from './draw-data';
import { MarkupDrawLifecycle } from './draw-lifecycle';
import { MarkupEdgelessPreview } from './edgeless-preview';
import { DRAW_EVENTS } from './event-name';
import { MarkupDrawPlugin } from './markup-draw';
import { normalizeDrawSegmentResult } from './segment-result';
import markupDrawPluginState, { getMarkupLayerState, useMarkupLayerState } from './states/markup-state';

function getSegmentDrawingPreviewLayer(layer: ILayerProps) {
    const nextLayer = cloneDeep(layer);
    const data = nextLayer.data as IMarkupLayerProps;
    const firstPoint = data.anchor.points?.[0];
    if (!firstPoint) return nextLayer;

    const labelRenderInfo = LabelKit.getLabelRenderInfoByLayer(nextLayer);
    const labelBoundsPadding = Math.max(10, data.fill.width);
    const textPaddingX = LabelKit.getRenderText(data.label)
        ? labelRenderInfo.renderWidth + labelRenderInfo.marginH + labelBoundsPadding
        : 0;
    const textPaddingY = LabelKit.getRenderText(data.label) ? labelRenderInfo.renderHeight / 2 + labelBoundsPadding : 0;
    const pointPadding = Math.max(
        data.fill.width,
        ((data.startPoint?.width || 0) / MARKUP_POINT_SIZE) * data.fill.width,
        ((data.startPoint?.height || 0) / MARKUP_POINT_SIZE) * data.fill.width,
    );

    const minX = Math.min(firstPoint.x, 0, -pointPadding) - labelBoundsPadding;
    const maxX = Math.max(firstPoint.x, 0, pointPadding, textPaddingX) + labelBoundsPadding;
    const minY = Math.min(firstPoint.y, 0, -pointPadding, -textPaddingY) - labelBoundsPadding;
    const maxY = Math.max(firstPoint.y, 0, pointPadding, textPaddingY) + labelBoundsPadding;

    Object.assign(nextLayer, {
        left: Math.ceil(data.centerOffset.x + minX),
        top: Math.ceil(data.centerOffset.y + minY),
        width: Math.ceil(maxX - minX),
        height: Math.ceil(maxY - minY),
        rotate: 0,
    });
    data.centerOffset = { x: -minX, y: -minY };

    return nextLayer;
}

/**
 * 适配标注笔的基础信息
 *
 * @param identity - ID
 * @param markupData - 标记数据
 * @param scale - 缩放比例
 * @param stageIndex - 可选；舞台序号；默认值为 `0`
 */
const adapterDrawMarkupBaseInfo = (identity: string, markupData: IMarkupDrawConfig) => {
    // 获取舞台大小的画布
    function getFullCanvas(info) {
        const { width, height } = getFramework(identity).service.editor.getBoundingClientRect();
        info.width = width;
        info.height = height;
        info.left = 0;
        info.top = 0;
        return info;
    }

    return {
        /** 箭头笔 */
        [MarkupType.Arrow](info) {
            // 计算直线的旋转角度
            function getLineTransform(info: any) {
                const { maxY, maxX, top: minY, left: minX, x1, x2, y1, y2 } = info;
                const centerPoint = {
                    x: (x2 - x1) / 2 + x1,
                    y: (y2 - y1) / 2 + y1,
                };
                const start = {
                    x: x2 - x1,
                    y: y2 - y1,
                };
                const rotate = -getPointsBetweenAngle(start, { x: 1, y: 0 });
                const line = Math.pow(maxY - minY, 2) + Math.pow(maxX - minX, 2);
                return {
                    centerPoint,
                    rotate: getActualAngle(rotate),
                    width: +Math.sqrt(line).toFixed(0),
                };
            }
            // 获取直线的基础信息
            function getLineBaseInfo(info, height = 0, minWidth = 0) {
                const { centerPoint, rotate, width } = getLineTransform(info);
                info.width = Math.max(width, minWidth);
                info.height = height;
                info.top = centerPoint.y - info.height / 2;
                info.left = centerPoint.x - info.width / 2;
                info.rotate = rotate;
                info.centerOffset = {
                    x: 0,
                    y: Math.max(markupData.anchor.arrowLeft[0], markupData.anchor.arrowRight[0]),
                };
                return info;
            }

            /**
             * 获取箭头的最小尺寸
             *
             * @param fillWidth - 填充宽度
             * @param startPoint - 开始坐标点
             * @param endPoint - 结束坐标点
             */
            function getArrowMinSize(
                fillWidth: number,
                startPoint: IMarkupPoint,
                endPoint: IMarkupPoint,
                anchor: IMarkupAnchor,
            ) {
                startPoint = startPoint || getDefaultMarkupPoint();
                endPoint = endPoint || getDefaultMarkupPoint();
                // 计算新的宽高，素材规范为 4px 大小
                const startPointHeight = (startPoint.height / MARKUP_POINT_SIZE) * fillWidth;
                const endPointHeight = (endPoint.height / MARKUP_POINT_SIZE) * fillWidth;
                const minHeight = Math.max(
                    startPointHeight,
                    endPointHeight,
                    fillWidth,
                    anchor.arrowLeft[0] - anchor.arrowLeft[1],
                    anchor.arrowRight[0] - anchor.arrowRight[1],
                );
                const startPointWidth = (startPoint.width / MARKUP_POINT_SIZE) * fillWidth;
                const endPointWidth = (endPoint.width / MARKUP_POINT_SIZE) * fillWidth;
                const minWidth = Math.max(startPointWidth, endPointWidth);
                return {
                    minHeight,
                    minWidth,
                };
            }

            const { minHeight, minWidth } = getArrowMinSize(
                markupData.fill.width,
                markupData.startPoint,
                markupData.endPoint,
                markupData.anchor,
            );

            return getLineBaseInfo(info, minHeight, minWidth);
        },

        [MarkupType.Segment](info) {
            getFullCanvas(info);
            return info;
        },
    };
};

/** 获取绘制的基础信息 */
function getDrawMarkupBaseInfo(identity: string, markupData: IMarkupDrawConfig, startPoint: IPoint, endPoint: IPoint) {
    const { x: x1, y: y1 } = startPoint;
    const { x: x2, y: y2 } = endPoint;
    const maxX = max([x1, x2]) as number;
    const minX = min([x1, x2]) as number;
    const maxY = max([y1, y2]) as number;
    const minY = min([y1, y2]) as number;
    const info = {
        width: maxX - minX,
        height: maxY - minY,
        left: minX,
        top: minY,
        rotate: 0,
        maxX,
        maxY,
        x1,
        x2,
        y1,
        y2,
        centerOffset: { x: 0, y: 0 },
    };

    // 重新适配基础信息
    adapterDrawMarkupBaseInfo(identity, markupData)[markupData.markupType](info);

    return info;
}

const DrawOperate: React.FC = React.memo(() => {
    // const mask = useRef<HTMLDivElement>(null);
    const identity = useIdentity();
    const activeStageIndex = useActiveFrameIndex();
    const framework = getFramework(identity);
    // const isInit = useRef(false);
    /** 绘制的鼠标点 */
    const mousePoints = useRef<IPoint[]>([]);
    /** 绘制时的预览图层 */
    const preview = useRef<ILayerProps>();
    /** 绘制时的预览图层 */
    const result = useRef<ILayerProps>();
    /** 画笔属性面板 */
    const drawConfig = useMarkupLayerState()((state) => state.drawConfig);
    /** 渲染预览图层数据 */
    const [previewLayer, setPreviewLayer] = useState<ILayerProps | null>(null);
    /** 自定义鼠标样式 */
    const [penPoint, setPenPoint] = useState<IPoint | null>(null);
    /** 第几次点击 */
    const clickCount = useRef(0);
    /** 计算label大小 */
    const labelKit = useRef<LabelKit>();
    /** 绘制异步任务的代际边界 */
    const drawLifecycle = useRef(new MarkupDrawLifecycle());
    /** 画笔实时属性 */
    const [markupData, setDrawMarkupData] = useState<IMarkupDrawConfig | null>(null);
    /** 是否正在绘制 */
    const drawing = useRef('');
    /** 当前绘制锁定的画布索引 */
    const drawingFrameIndex = useRef<number>();
    /** 当前绘制锁定的稳定画布 ID */
    const drawingFrameId = useRef<number>();
    /** 绘制操作类型 */
    const drawOperateType = {
        click: 'click',
        mouse: 'mouse',
    };
    const { $t } = useLocaleStore();
    const drawTips = {
        tip: $t((v) => v.brushLayer.tips.moveToCanvas),
        [MarkupType.Arrow]: {
            0: $t((v) => v.markupLayer.tips.firstClick),
            1: $t((v) => v.markupLayer.tips.secondClick),
            2: $t((v) => v.markupLayer.tips.thirdClick),
        },
        [MarkupType.Segment]: {
            0: $t((v) => v.markupLayer.tips.firstClick),
        },
    };

    function resetDrawInteraction() {
        mousePoints.current.length = 0;
        preview.current = undefined;
        result.current = undefined;
        clickCount.current = 0;
        drawing.current = '';
        labelKit.current = undefined;
        drawingFrameIndex.current = undefined;
        drawingFrameId.current = undefined;
        setPreviewLayer(null);
        setPenPoint(null);
    }

    /** 根据舞台大小，等比例缩放配置 */
    useEffect(() => {
        drawLifecycle.current.reset();
        resetDrawInteraction();
        if (drawConfig) {
            const currentWidth = framework.service.frame.getSizeByIndex(activeStageIndex).width || 1242;
            setDrawMarkupData(adaptDrawMarkupConfig(drawConfig, currentWidth));
        } else {
            setDrawMarkupData(null);
        }
    }, [activeStageIndex, drawConfig]);

    useEffect(() => {
        const lifecycle = drawLifecycle.current;
        return () => {
            lifecycle.reset();
        };
    }, []);

    function createPreviewLayer() {
        if (!markupData) return;
        const generation = drawLifecycle.current.currentGeneration;
        const layer = getMarkupDefaultLayer(markupData, true);
        preview.current = layer;
        labelKit.current = new LabelKit();
        const fontReadyPromise = prepareMarkupLayerDocumentFonts(framework.service, layer);
        drawLifecycle.current.setFontReadyPromise(generation, fontReadyPromise);
        fontReadyPromise
            .then(() => {
                if (!drawLifecycle.current.isCurrent(generation)) return;
                if (preview.current !== layer || !clickCount.current) return;
                const targetLayer = result.current || preview.current;
                if (targetLayer !== layer && layer.__runtime?.fontFamilyMap) {
                    setLayerRuntimeValue(targetLayer, { fontFamilyMap: cloneDeep(layer.__runtime.fontFamilyMap) });
                }
                refreshPreviewLabelSize(targetLayer);
                setPreviewLayer(getPreviewRenderLayer(targetLayer));
            })
            .catch(() => {});
    }

    // 创建绘制预览图层
    useEffect(() => {
        if (markupData) {
            createPreviewLayer();
        }
    }, [markupData]);

    function clearDrawConfig() {
        getMarkupLayerState(identity).setDrawConfig(null);
    }

    useEffect(() => {
        if (!drawConfig) return;
        const handle = framework.service.hotkey.onEsc(() => {
            clearDrawConfig();
        });
        return () => {
            framework.service.events.off(handle);
        };
    }, [drawConfig]);

    // 操纵绘制
    useEffect(() => {
        if (!markupData) return;
        // 禁用图层选中事件，取消激活图层
        framework.service.editor.setDisablePick(true);
        framework.service.frame.delHoverId();
        framework.service.frame.setPickActiveFrame(false);

        const drawType = drawOperateType.click;
        // 绑定点击事件
        const clickHandle = (e) => {
            if (!markupData) return;
            switch (clickCount.current) {
                case 0:
                    if (!drawing.current) {
                        firstClick(e, drawType);
                        showDrawTip(e);
                    }
                    break;
                case 1:
                    if (drawing.current === drawType) {
                        secondClick(e);
                        showDrawTip(e);
                        drawPreview(e);
                        void convertPreviewPropsToLayer();
                    }
                    break;
                case 2:
                    if (drawing.current === drawType) {
                        thirdClick(e);
                        showDrawTip(e);
                        drawPreview(e);
                        void convertPreviewPropsToLayer();
                    }
                    break;
            }
            // showDrawTip(e);
        };

        const mouseEnterHandler = framework.service.editor.onMouseEnter((originIndex) => {
            const isDrawing = clickCount.current !== 0;
            if (
                drawingFrameIndex.current === undefined &&
                !isDrawing &&
                framework.service.edgelessCanvas.isLongImageMode
            ) {
                framework.service.frame.setActiveIndex(framework.service.frame.frameIdToIndex(originIndex));
            }
        });

        // 绑定绘制事件
        const mouseMoveHandle = debounce(
            (e) => {
                if (!markupData) return;
                if (drawing.current === drawType) {
                    drawPreview(e);
                }
                const mPoint = { x: e.x, y: e.y };
                const isDrawing = clickCount.current !== 0;
                const stageIndex = resolveDrawFrameIndex(mPoint);
                const isInStage = framework.service.transform.checkPointInFrame(mPoint, stageIndex);
                if (isDrawing || isInStage) {
                    setPenPoint(mPoint);
                } else {
                    if (!isDrawing) {
                        setPenPoint(null);
                    }
                }
                showDrawTip(e);
            },
            5,
            { leading: true },
        );
        const mouseDownHandle = (e) => {
            showDrawTip(e, true);
        };
        document.addEventListener('click', clickHandle);
        document.addEventListener('mousedown', mouseDownHandle);
        document.addEventListener('mousemove', mouseMoveHandle);
        return () => {
            framework.service.registration.getInstance<Displacement>('Displacement')?.clear();
            framework.service.editor.setDisablePick(false);
            framework.service.events.off(mouseEnterHandler);
            document.removeEventListener('click', clickHandle);
            document.removeEventListener('mousedown', mouseDownHandle);
            document.removeEventListener('mousemove', mouseMoveHandle);
        };
    }, [markupData]);

    useMoveEventListener(
        (e: ITouchKitEvent) => {
            if (!markupData) return;
            const drawType = drawOperateType.mouse;
            if (e.isStart && !drawing.current) {
                const stageIndex = resolveDrawFrameIndex(e.startPoint);
                const startInStage = framework.service.transform.checkPointInFrame(e.startPoint, stageIndex);
                if (startInStage) {
                    firstClick(e, drawType);
                }
            }
            if (drawing.current === drawType) {
                drawPreview(e);
            }
            if (e.isEnd && drawing.current === drawType) {
                secondClick(e);
                drawPreview(e);
                void convertPreviewPropsToLayer();
                setTimeout(() => {
                    drawing.current = drawOperateType.click;
                }, 10);
            }
        },
        MoveListenerLevel.DEFAULT_LEVEL,
        'markup-draw-default',
    );

    // 绘制提示
    function showDrawTip(e: IPoint, needDestroy = false) {
        if (!markupData) return;
        const mPoint = { x: e.x, y: e.y };
        if (!framework.service.transform.checkPointInViewport(mPoint)) {
            framework.service.registration.getInstance<Displacement>('Displacement')?.clear();
            return;
        }
        const stageIndex = resolveDrawFrameIndex(mPoint);
        const isInStage = framework.service.transform.checkPointInFrame(mPoint, stageIndex);
        const isDrawing = clickCount.current !== 0;
        if (!isInStage && !isDrawing) {
            if (needDestroy) {
                clearDrawConfig();
            }
            framework.service.registration.getInstance<Displacement>('Displacement')?.set({
                type: 'custom',
                x: mPoint.x,
                y: mPoint.y,
                txt: drawTips.tip,
            });
        } else {
            const tip = drawTips[markupData.markupType]?.[clickCount.current];
            if (tip) {
                framework.service.registration.getInstance<Displacement>('Displacement')?.set({
                    type: 'custom',
                    x: mPoint.x,
                    y: mPoint.y,
                    txt: tip,
                });
            } else {
                framework.service.registration.getInstance<Displacement>('Displacement')?.clear();
            }
        }
    }

    /** 仅在长图模式下按鼠标位置同步画布；无限画布保持原绘制逻辑 */
    function resolveDrawFrameIndex(point: IPoint) {
        if (drawingFrameIndex.current !== undefined) {
            return drawingFrameIndex.current;
        }
        const activeFrameIndex = framework.service.frame.getActiveIndex();
        if (!framework.service.edgelessCanvas.isLongImageMode) {
            return activeFrameIndex;
        }
        const frameIndex = framework.service.transform.getFrameIndexByMousePoint(point);
        if (frameIndex === undefined) {
            return activeFrameIndex;
        }
        if (frameIndex !== activeFrameIndex) {
            framework.service.frame.setActiveIndex(frameIndex);
        }
        return frameIndex;
    }

    function collectMousePoints(e) {
        e = e.originEvent || e;
        const point = framework.service.transform.getScenePoint(e);
        mousePoints.current.push(point);
    }

    // 第一次点击
    function firstClick(e, type: string) {
        e = e.originEvent || e;
        const stageIndex = resolveDrawFrameIndex(e);
        const inStage = framework.service.transform.checkPointInFrame(e, stageIndex);
        if (!inStage) return false;
        if (framework.service.edgelessCanvas.isLongImageMode) {
            drawingFrameIndex.current = stageIndex;
            drawingFrameId.current = resolveDrawingFrameId(
                framework.service.edgelessCanvas.isLongImageMode,
                stageIndex,
                (frameIndex) => framework.service.frame.indexToFrameId(frameIndex),
            );
        }
        drawing.current = type;
        clickCount.current = 0;
        mousePoints.current.length = 0;
        result.current = undefined;
        clickCount.current += 1;

        collectMousePoints(e);

        if (!preview.current) {
            createPreviewLayer();
        }
        drawPreview(e);
        return true;
    }
    // 第二次点击
    function secondClick(e) {
        e = e.originEvent || e;
        if (!preview.current) return;
        clickCount.current += 1;

        collectMousePoints(e);
    }
    // 第三次点击
    function thirdClick(e) {
        // e = e.originEvent || e;
        if (!preview.current) return;
        clickCount.current += 1;
    }

    function fixLinePoint(point: IPoint) {
        // 绘制 90 180 的角度
        const hotKeyPress = isHotKeyPress();
        if (hotKeyPress) {
            const firstPoint = mousePoints.current[0];
            const xLength = Math.abs(point.x - firstPoint.x);
            const yLength = Math.abs(point.y - firstPoint.y);
            if (xLength > yLength) {
                point.y = firstPoint.y;
            } else {
                point.x = firstPoint.x;
            }
        }
    }

    /** 执行绘制 */
    function drawPreview(e) {
        e = e.originEvent || e;
        if (preview.current && markupData) {
            const previewProps = preview.current;
            const currentPoint = framework.service.transform.getScenePoint(e);

            if (clickCount.current === 1) {
                fixLinePoint(currentPoint);
                // 第2个点的绘制
                const info = getDrawMarkupBaseInfo(identity, markupData, mousePoints.current[0], currentPoint);
                // console.log(info);
                Object.assign(previewProps, {
                    width: ~~info.width,
                    height: ~~info.height,
                    left: ~~info.left,
                    top: ~~info.top,
                    rotate: info.rotate,
                });

                // 实时调整label的大小
                if (markupData.markupType === MarkupType.Arrow) {
                    Object.assign(previewProps.data, {
                        centerOffset: { ...info.centerOffset },
                    });
                }

                // 实时调整绘制点坐标
                if (markupData.markupType === MarkupType.Segment) {
                    const pointStart = subPoint(mousePoints.current[0], currentPoint);
                    Object.assign(previewProps.data, {
                        centerOffset: currentPoint,
                        anchor: {
                            points: [pointStart],
                        },
                    });
                }
                refreshPreviewLabelSize(previewProps);
                // console.log('previewProps', cloneDeep(previewProps));

                setPreviewLayer(getPreviewRenderLayer(previewProps));
            }

            if (clickCount.current === 2 || (!result.current && clickCount.current === markupData?.__drawClick)) {
                if (markupData.markupType === MarkupType.Arrow) {
                    result.current = adjustDrawArrowThreeOperate(
                        cloneDeep(previewProps),
                        previewProps,
                        mousePoints.current[1],
                        currentPoint,
                    );
                    setPreviewLayer(getPreviewRenderLayer(result.current));
                }

                // 实时调整绘制点坐标
                if (markupData.markupType === MarkupType.Segment) {
                    const data = previewProps.data as IMarkupLayerProps;
                    const pointEnd = subPoint(currentPoint, data.centerOffset);
                    Object.assign(previewProps.data, {
                        anchor: {
                            points: [data.anchor.points![0], pointEnd],
                        },
                    });
                    result.current = cloneDeep(previewProps);
                    setPreviewLayer(getPreviewRenderLayer(result.current));
                }
            }
        }
    }

    function refreshPreviewLabelSize(layer: ILayerProps) {
        if (!markupData) return;
        const data = layer.data as IMarkupLayerProps;
        createLabelKit();

        if (data.markupType === MarkupType.Arrow) {
            applyDrawLabelLayout(layer, adjustDrawArrowTowOperate(layer), LabelKit.getRoundWeight);
        }

        if (data.markupType === MarkupType.Segment) {
            applyDrawLabelLayout(layer, labelKit.current?.getSize(layer), LabelKit.getRoundWeight);
        }
    }

    /** 箭头标注，调整第2个操作 label 大小 */
    function adjustDrawArrowTowOperate(layer: ILayerProps) {
        if (!markupData) return;
        createLabelKit();

        return labelKit.current ? getDrawArrowLabelSize(layer, markupData.label.fontSize, labelKit.current) : null;
    }
    /** 箭头标注，第3个操作图层大小 */
    function adjustDrawArrowThreeOperate(
        newLayer: ILayerProps,
        originLayer: ILayerProps,
        startPoint: IPoint,
        currentPoint: IPoint,
    ) {
        startPoint = translatePointAngle(startPoint, -newLayer.rotate);
        const endPoint = translatePointAngle(currentPoint, -newLayer.rotate);
        const operateType = startPoint.y <= endPoint.y ? ResizeOperateType.BC : ResizeOperateType.TC;
        const direction = operateType === ResizeOperateType.TC ? DirectionType.OPPOSITE : DirectionType.POSITIVE;
        const isTC = operateType === ResizeOperateType.TC;
        const top = endPoint.y - startPoint.y;
        const offset = Math.floor(top * direction);
        newLayer.height = offset + originLayer.height;
        const newCenter = getNewCenterPoint(originLayer as any, newLayer, isTC, 1);
        newLayer.left = newCenter.x - newLayer.width / 2;
        newLayer.top = newCenter.y - newLayer.height / 2;

        const newLayerData = newLayer.data as IMarkupLayerProps;
        const originData = originLayer.data as IMarkupLayerProps;
        if (isTC) {
            newLayerData.anchor.arrowLeft[1] = Math.min(
                originData.anchor.arrowLeft[1] - offset,
                originData.anchor.arrowLeft[1],
            );
            newLayerData.anchor.arrowRight[1] = Math.min(
                originData.anchor.arrowRight[1] - offset,
                originData.anchor.arrowRight[1],
            );
        } else {
            newLayerData.anchor.arrowLeft[0] = Math.max(
                originData.anchor.arrowLeft[0] + offset,
                originData.anchor.arrowLeft[0],
            );
            newLayerData.anchor.arrowRight[0] = Math.max(
                originData.anchor.arrowRight[0] + offset,
                originData.anchor.arrowRight[0],
            );
            newLayerData.centerOffset.y = Math.max(newLayerData.anchor.arrowLeft[0], newLayerData.anchor.arrowRight[0]);
        }

        return newLayer;
    }
    /**
     * 修正尺寸图绘制最终层宽高，定位
     *
     * @param layer - 图层属性
     */
    function fixDrawArrowResult(layer: ILayerProps) {
        const data = layer.data as IMarkupLayerProps;
        const {
            fill,
            anchor: { arrowLeft, arrowRight },
            startPoint,
            endPoint,
        } = data;
        const {
            renderArea: { up, down },
        } = LabelKit.getLabelRenderInfo(layer.width, fill.width, layer);
        const leftPointHeight = startPoint ? (startPoint.height / MARKUP_POINT_SIZE) * fill.width : 0;
        const rightPointHeight = endPoint ? (endPoint.height / MARKUP_POINT_SIZE) * fill.width : 0;
        const halfPointHeight = Math.ceil(Math.max(leftPointHeight / 2, rightPointHeight / 2));
        const upOrigin = Math.max(arrowLeft[0], arrowRight[0], halfPointHeight);
        const upResult = Math.max(up, upOrigin);
        const downOrigin = Math.min(arrowLeft[1], arrowRight[1], -halfPointHeight);
        const downResult = Math.min(down, downOrigin);
        // console.log(upResult, upOrigin, upResult - upOrigin);
        if (upResult > upOrigin) {
            const offset = upResult - upOrigin;
            // console.log(offset);

            const { left, top, width, height, rotate } = layer;
            layer.height += offset;
            const newCenter = getNewCenterPoint(
                { left, top, width, height, rotate },
                { width, height: layer.height },
                true,
                1,
            );
            layer.left = newCenter.x - width / 2;
            layer.top = newCenter.y - layer.height / 2;
            data.centerOffset.y = upResult;
        }
        if (downResult < downOrigin) {
            const offset = downOrigin - downResult;
            const { left, top, width, height, rotate } = layer;
            layer.height += offset;
            const newCenter = getNewCenterPoint(
                { left, top, width, height, rotate },
                { width, height: layer.height },
                false,
                1,
            );
            layer.left = newCenter.x - width / 2;
            layer.top = newCenter.y - layer.height / 2;
        }
    }
    function getPreviewRenderLayer(layer: ILayerProps) {
        const renderLayer = cloneDeep(layer);
        const data = renderLayer.data as IMarkupLayerProps;
        if (data.markupType === MarkupType.Arrow) {
            fixDrawArrowResult(renderLayer);
            return renderLayer;
        }
        if (data.markupType === MarkupType.Segment) {
            if (data.anchor.points?.[1]) {
                normalizeDrawSegmentResult(renderLayer);
                return renderLayer;
            }
            return getSegmentDrawingPreviewLayer(renderLayer);
        }
        return renderLayer;
    }

    /**
     * 转换成真实图层
     */
    async function convertPreviewPropsToLayer() {
        const generation = drawLifecycle.current.currentGeneration;
        const targetFrameId = drawingFrameId.current;
        const resultLayer = result.current;
        if (!resultLayer) return;
        if (isNumber(markupData?.__drawClick) && markupData.__drawClick > clickCount.current) return;

        const finalizeResult = await drawLifecycle.current.beginFinalize(generation);
        if (!finalizeResult.shouldFinalize || !drawLifecycle.current.isCurrent(generation)) return;

        const layer = cloneDeep(resultLayer);
        const data = layer.data as IMarkupLayerProps;
        const markupType = data.markupType;
        if (preview.current?.__runtime?.fontFamilyMap) {
            setLayerRuntimeValue(layer, {
                fontFamilyMap: cloneDeep(preview.current.__runtime.fontFamilyMap),
            });
        }
        labelKit.current = new LabelKit();
        delete layer.__runtime?.isDrawPreview;
        refreshPreviewLabelSize(layer);
        if (markupType === MarkupType.Arrow) {
            fixDrawArrowResult(layer);
        }
        if (markupType === MarkupType.Segment) {
            normalizeDrawSegmentResult(layer);
        }
        if (markupData?.__vipFuncType) {
            if (!layer.materialIds) {
                layer.materialIds = {};
            }
            const funcList = layer.materialIds[MaterialIdsType.FUNC_LIST] || [];
            funcList.push(markupData?.__vipFuncType);
            layer.materialIds[MaterialIdsType.FUNC_LIST] = funcList;
        }
        setLayerRuntimeValue(layer, {
            isInit: true,
        });

        if (!drawLifecycle.current.isCurrent(generation)) return;
        const drawFrameTarget = resolveDrawFrameTarget({
            isLongImageMode: framework.service.edgelessCanvas.isLongImageMode,
            drawingFrameId: targetFrameId,
            checkFrameIsRender: (frameId) => framework.service.frame.checkFrameIsRender(frameId),
            getFrameOffsetTop: (frameId) => framework.service.frame.getFrameTransform(frameId).offsetTop,
        });
        if (drawFrameTarget.type === 'cancel') {
            cancelCurrentDraw({
                generation,
                isCurrent: (currentGeneration) => drawLifecycle.current.isCurrent(currentGeneration),
                resetInteraction: resetDrawInteraction,
                clearDrawConfig,
            });
            return;
        }
        const markupLayer = framework.service.registration.getInstance<MarkupLayer>('MarkupLayer');
        const drawLayerArgs =
            drawFrameTarget.type === 'target' ? applyDrawFrameTarget(layer, drawFrameTarget) : { layer };
        const resLayer = markupLayer.add({
            ...drawLayerArgs,
            trackFunc: trackConfig[LayerType.BRUSH_LAYER][`add_${markupType}`],
            checkFocus: true,
        });
        // 绘制结束
        updateScreenshotUrl(identity, resLayer.id);
        framework.service.events.emit(DRAW_EVENTS.DRAW_END, markupType);

        setTimeout(() => {
            if (drawLifecycle.current.isCurrent(generation)) {
                clearDrawConfig();
            }
        }, 50);
    }

    /**
     * 创建文字工具
     */
    function createLabelKit() {
        if (!labelKit.current) {
            labelKit.current = new LabelKit();
        }
    }

    if (!penPoint || !markupData) return null;

    const stagePoint = framework.service.transform.getViewportPoint(penPoint);

    return (
        <BaseStagePlugin name="markup-draw">
            <RenderPen x={stagePoint.x} y={stagePoint.y} />
            {previewLayer ? <MarkupEdgelessPreview layer={previewLayer} identity={identity} /> : null}
        </BaseStagePlugin>
    );
});

const RenderPen: React.FC<IPoint> = React.memo(({ x, y }) => {
    return (
        <div className="markup-draw-mask" data-operate-type={BaseLayerViewType.MASK}>
            <div className="markup-draw-mask-pen" style={{ transform: `translate(${x}px, ${y}px)` }}>
                <img src={CurPenSvg} width={16} height={16} />
            </div>
        </div>
    );
});

const DrawPluginView: React.FC = React.memo(() => {
    const drawConfig = useMarkupLayerState()((state) => state.drawConfig);
    const identity = useIdentity();
    const isInit = useRef(false);
    const framework = getFramework(identity);

    useEffect(() => {
        if (!isInit.current) {
            isInit.current = true;
            return;
        }
        framework.service.events.emit(DRAW_EVENTS.CONFIG_CHANGE, drawConfig);
    }, [drawConfig]);
    if (!drawConfig) return null;
    return <DrawOperate />;
});

const exportPlugin: IExportPlugin = {
    type: PluginType.STAGE,
    sort: 9,
    name: 'markup-draw',
    render() {
        return <DrawPluginView />;
    },
    createState() {
        return [markupDrawPluginState];
    },
    createInstance({ identity }) {
        return [{ key: 'MarkupDrawPlugin', value: new MarkupDrawPlugin(identity) }];
    },
};

export default exportPlugin;
